import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Flex, Group, Paper, Stack, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { EmailOutlined, PersonOutlined, PhoneOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useEffect } from "react";
import { useCreateUserMutation, useGetTeamMemberQuery, useUpdateUserMutation } from "../../graphql/graphql";
import { formatPhoneNumber } from "../../utils/PhoneNumber.utils";
import { CopyButton } from "../../shared/CopyComponents/CopyButton";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { useTherapproveDrawers } from "../../shared/TherapproveOverlays";
import { CaregiverInitialValues, CaregiverSchema } from "./CaregiverDrawer.validation";
// ─── Subcomponents ──────────────────────────────────────────────────
const Section = ({ icon, title, children }) => (_jsx(Paper, { radius: "md", p: "md", withBorder: true, style: { borderColor: "var(--mantine-color-gray-2)" }, children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Group, { gap: "xs", children: [_jsx(SvgIcon, { component: icon, style: {
                            fontSize: "1rem",
                            color: "var(--mantine-color-primary-5)"
                        } }), _jsx(Text, { size: "sm", fw: 600, c: "dimmed", children: title })] }), children] }) }));
// ─── Main component ─────────────────────────────────────────────────
export const CaregiverDrawerFormContent = ({ caregiverId, active = true, onSuccess, onDirtyChange }) => {
    const { patient } = useTherapproveDrawers();
    const isCreate = !caregiverId;
    // Mutations
    const [{ fetching: createFetching }, createUser] = useCreateUserMutation();
    const [{ fetching: updateFetching }, updateUser] = useUpdateUserMutation();
    // Queries
    const [{ data }] = useGetTeamMemberQuery({
        requestPolicy: "cache-and-network",
        variables: { userId: caregiverId },
        pause: !caregiverId || !active
    });
    const caregiver = data?.users?.user;
    const patients = caregiver?.patients ?? [];
    // Form
    const form = useForm({
        initialValues: CaregiverInitialValues,
        validate: yupResolver(CaregiverSchema)
    });
    const isDirty = form.isDirty();
    const fetching = createFetching || updateFetching;
    // Effects
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    useEffect(() => {
        if (!active)
            return;
        if (isCreate) {
            form.setValues(CaregiverInitialValues);
        }
        else if (caregiver) {
            form.setValues({
                firstName: caregiver.firstName,
                lastName: caregiver.lastName,
                email: caregiver.email,
                phoneNumber: caregiver.phoneNumber
            });
        }
        form.resetDirty();
    }, [active, caregiverId, caregiver]);
    // Handlers
    const handleSubmit = async (event) => {
        event.preventDefault();
        const values = form.values;
        if (!values.firstName || !values.lastName || !values.email || !values.phoneNumber) {
            showErrorNotification("Please fill in all required fields.");
            return;
        }
        try {
            if (isCreate) {
                const result = await createUser({
                    input: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phoneNumber: values.phoneNumber
                    }
                });
                if (!result.data?.users?.createUser) {
                    throw new Error("Failed to create primary contact.");
                }
                showSuccessNotification(`${values.firstName} ${values.lastName} was added.`);
            }
            else {
                const result = await updateUser({
                    input: {
                        userId: caregiverId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phoneNumber: values.phoneNumber
                    }
                });
                if (!result.data?.users?.updateUser) {
                    throw new Error("Failed to update primary contact.");
                }
                showSuccessNotification(`${values.firstName} ${values.lastName} was updated.`);
            }
            form.resetDirty();
            onSuccess?.();
        }
        catch (error) {
            console.error(error);
            showErrorNotification();
        }
    };
    // Select all text on focus so typing replaces the existing value
    const selectAllOnFocus = (e) => {
        const selection = window.getSelection();
        if (!selection)
            return;
        const range = document.createRange();
        range.selectNodeContents(e.currentTarget);
        selection.removeAllRanges();
        selection.addRange(range);
    };
    const cleanEmpty = (e) => {
        const el = e.currentTarget;
        if (!el.textContent?.trim()) {
            el.innerHTML = "";
        }
    };
    return (_jsxs(Stack, { gap: 0, style: { flex: 1, minHeight: 0 }, children: [_jsx("div", { style: { flex: 1, overflowY: "auto", overflowX: "hidden" }, children: _jsxs(Stack, { gap: "md", pb: "md", children: [_jsx(Box, { py: "lg", px: "md", style: { backgroundColor: "var(--mantine-color-primary-0)" }, children: _jsxs(Stack, { gap: 4, children: [_jsxs(Group, { gap: 6, wrap: "wrap", children: [_jsx(Text, { fw: 700, style: { fontSize: "1.5rem", whiteSpace: "nowrap" }, c: "gray.9", contentEditable: true, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "First", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                    form.setFieldValue("firstName", (e.currentTarget.textContent ?? "").trim());
                                                }, children: form.values.firstName || null }), _jsx(Text, { fw: 700, style: { fontSize: "1.5rem", whiteSpace: "nowrap" }, c: "gray.9", contentEditable: true, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Last", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                    form.setFieldValue("lastName", (e.currentTarget.textContent ?? "").trim());
                                                }, children: form.values.lastName || null })] }), _jsx(Text, { size: "xs", c: "dimmed", children: "Primary Contact" })] }) }), _jsxs(Stack, { gap: "sm", px: "md", children: [_jsx(Section, { icon: PersonOutlined, title: "Contact", children: _jsxs(Stack, { gap: 0, align: "flex-start", children: [_jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(SvgIcon, { component: PhoneOutlined, style: {
                                                            fontSize: "0.9rem",
                                                            color: "var(--mantine-color-gray-5)",
                                                            flexShrink: 0
                                                        } }), _jsx(Text, { size: "sm", c: "gray.8", fw: 600, style: { whiteSpace: "nowrap" }, contentEditable: true, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Phone number", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                            form.setFieldValue("phoneNumber", formatPhoneNumber((e.currentTarget.textContent ?? "").trim()));
                                                        }, children: form.values.phoneNumber || null }), form.values.phoneNumber && (_jsx(CopyButton, { value: form.values.phoneNumber }))] }), _jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(SvgIcon, { component: EmailOutlined, style: {
                                                            fontSize: "0.9rem",
                                                            color: "var(--mantine-color-gray-5)",
                                                            flexShrink: 0
                                                        } }), _jsx(Text, { size: "sm", c: "gray.8", fw: 600, style: { whiteSpace: "nowrap" }, contentEditable: true, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Email address", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                            form.setFieldValue("email", (e.currentTarget.textContent ?? "").trim().toLowerCase());
                                                        }, children: form.values.email || null }), form.values.email && (_jsx(CopyButton, { value: form.values.email }))] })] }) }), !isCreate && patients.length > 0 && (_jsx(Section, { icon: PersonOutlined, title: "Children", children: _jsx(Stack, { gap: "xs", children: patients.map((p) => (_jsxs(Group, { gap: "xs", style: { cursor: "pointer" }, onClick: () => patient.open({ patientId: p.patientId }), children: [_jsxs(Text, { size: "sm", fw: 500, c: "gray.8", children: [p.firstName, " ", p.lastName] }), p.postalCodeInfo && (_jsxs(Text, { size: "xs", c: "dimmed", children: [p.postalCodeInfo.city, ", ", p.postalCodeInfo.stateId] }))] }, p.patientId))) }) }))] })] }) }), isDirty && (_jsxs(Flex, { gap: "sm", px: "md", py: "sm", style: {
                    borderTop: "1px solid var(--mantine-color-gray-2)",
                    backgroundColor: "var(--mantine-color-body)"
                }, children: [_jsx(Button, { variant: "filled", loading: fetching, onClick: handleSubmit, children: isCreate ? "Add Contact" : "Save Changes" }), _jsx(Button, { variant: "subtle", c: "dimmed", onClick: () => {
                            form.reset();
                            form.resetDirty();
                        }, children: "Cancel" })] }))] }));
};
